import { request } from '@/lib/api'

/**
 * Инфраструктура ИИ (тикет #126): подключение провайдера, включение на организацию, расход,
 * журнал вызовов и реестр промптов.
 *
 * Поля ответа сравниваются нестрого (`== null`): сервер не кладёт в JSON пустые поля вовсе
 * (`non_null`), и `undefined` здесь — обычное «не заполнено».
 */

export interface AiProvider {
  code: string
  name: string
  /** Без этих настроек провайдера не вызвать: YandexGPT — `folderId`. */
  requiredOptions: string[]
}

export interface AiOrganization {
  id: string
  name: string
  enabled: boolean
}

export interface AiConnection {
  enabled: boolean
  provider?: string | null
  endpoint?: string | null
  model?: string | null
  embeddingModel?: string | null
  folderId?: string | null
  scope?: string | null
  authEndpoint?: string | null
  /** Ключ сохранён; сам ключ сервер не отдаёт никогда. */
  secretSet: boolean
  userMonthlyTokens?: number | null
  priceInputPer1k?: number | null
  priceOutputPer1k?: number | null
  priceEmbeddingPer1k?: number | null
  timeoutSeconds: number
  providers?: AiProvider[]
  organizations?: AiOrganization[]
}

export interface AiConnectionUpdate {
  enabled: boolean
  provider: string | null
  endpoint: string | null
  model: string | null
  embeddingModel: string | null
  folderId: string | null
  scope: string | null
  authEndpoint: string | null
  /** `undefined` — оставить сохранённый, пустая строка — стереть. */
  secret?: string
  userMonthlyTokens: number | null
  priceInputPer1k: number | null
  priceOutputPer1k: number | null
  priceEmbeddingPer1k: number | null
  timeoutSeconds: number | null
}

export interface AiCheckResult {
  status: 'ok' | 'rejected' | 'unavailable'
  message?: string | null
  model?: string | null
  inputTokens?: number | null
  outputTokens?: number | null
}

export interface AiUserUsage {
  userId?: string | null
  name?: string | null
  calls: number
  failures: number
  inputTokens: number
  outputTokens: number
  /** Нет — цена за токены не задана хотя бы у одного вызова: сумма неизвестна, а не ноль. */
  cost?: number | null
}

export interface AiUsage {
  month: string
  userMonthlyTokens?: number | null
  users?: AiUserUsage[]
}

export type AiCallStatus = 'pending' | 'succeeded' | 'failed' | 'rejected'

export interface AiCall {
  id: string
  createdAt: string
  userName?: string | null
  organizationName?: string | null
  operation: 'complete' | 'embed'
  promptCode?: string | null
  promptVersion?: number | null
  provider: string
  model?: string | null
  status: AiCallStatus
  maskedCount: number
  inputTokens?: number | null
  outputTokens?: number | null
  cost?: number | null
  error?: string | null
}

export interface AiPromptVersion {
  id: string
  code: string
  version: number
  description?: string | null
  systemText?: string | null
  userTemplate: string
  responseFormat: 'TEXT' | 'JSON'
  temperature?: number | null
  maxTokens?: number | null
  active: boolean
  createdAt: string
  variables?: string[]
}

export interface AiPrompt {
  code: string
  activeVersion?: number | null
  versions?: AiPromptVersion[]
}

export interface AiPromptFields {
  description: string | null
  systemText: string | null
  userTemplate: string
  responseFormat: 'text' | 'json'
  temperature: number | null
  maxTokens: number | null
}

export function fetchAiConnection(signal?: AbortSignal): Promise<AiConnection> {
  return request<AiConnection>('/admin/integrations/ai', { signal })
}

export function updateAiConnection(update: AiConnectionUpdate): Promise<AiConnection> {
  return request<AiConnection>('/admin/integrations/ai', { method: 'PUT', body: update })
}

export function switchAiOrganization(id: string, enabled: boolean): Promise<AiConnection> {
  return request<AiConnection>(`/admin/integrations/ai/organizations/${id}`, { method: 'PUT', body: { enabled } })
}

export function checkAiConnection(): Promise<AiCheckResult> {
  return request<AiCheckResult>('/admin/integrations/ai/check', { method: 'POST', body: {} })
}

export function fetchAiUsage(month: string | null, signal?: AbortSignal): Promise<AiUsage> {
  return request<AiUsage>(`/admin/ai/usage${month ? `?month=${encodeURIComponent(month)}` : ''}`, { signal })
}

export function fetchAiCalls(signal?: AbortSignal): Promise<AiCall[]> {
  return request<{ calls?: AiCall[] }>('/admin/ai/calls?limit=50', { signal }).then((body) => body.calls ?? [])
}

export function fetchAiPrompts(signal?: AbortSignal): Promise<AiPrompt[]> {
  return request<{ prompts?: AiPrompt[] }>('/admin/ai/prompts', { signal }).then((body) => body.prompts ?? [])
}

export function createAiPromptVersion(code: string, fields: AiPromptFields): Promise<AiPromptVersion> {
  return request<AiPromptVersion>(`/admin/ai/prompts/${encodeURIComponent(code)}/versions`, { method: 'POST', body: fields })
}

export function activateAiPromptVersion(code: string, version: number): Promise<AiPromptVersion> {
  return request<AiPromptVersion>(`/admin/ai/prompts/${encodeURIComponent(code)}/active`, { method: 'PUT', body: { version } })
}
